import { useTranslation } from "react-i18next";
import type { LabelListContent } from "../../../api/content/authoring/processContentDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import MarkdownField from "../../../ui/forms/MarkdownField";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";

type Label = LabelListContent["labels"][number];

export default function LabelFields({ label, index, count, check, onChange, onMove, onRemove }: {
  label: Label;
  index: number;
  count: number;
  check: DefinitionCheck;
  onChange: (label: Label) => void;
  onMove: (offset: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <FieldGroup legend={t("authoring.labelList.label", { position: index + 1 })} className="app-card space-y-2 p-3">
      <div className="flex flex-wrap gap-2">
        <AppButton variant="ghost" size="xs" disabled={index === 0} onClick={() => { onMove(-1); }}>{t("authoring.labelList.moveUp")}</AppButton>
        <AppButton variant="ghost" size="xs" disabled={index === count - 1} onClick={() => { onMove(1); }}>{t("authoring.labelList.moveDown")}</AppButton>
        <AppButton variant="ghost" size="xs" onClick={onRemove}>{t("authoring.labelList.removeLabel")}</AppButton>
      </div>
      {(["key", "name", "description"] as const).map((field) => (
        <Field key={field} label={t(`authoring.labelList.fields.${field}`)} errors={<FieldProblems check={check} path={`labels.${index}.${field}`} />}>
          {(control) =>
            field === "description" ? (
              <textarea {...control} className={FORM_COMPACT_CONTROL_CLASS} rows={3} maxLength={20000} value={label[field]} onChange={(event) => { onChange({ ...label, [field]: event.target.value }); }} />
            ) : (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} maxLength={field === "key" ? 63 : 200} value={label[field]} onChange={(event) => { onChange({ ...label, [field]: event.target.value }); }} />
            )
          }
        </Field>
      ))}
      <MarkdownField label={t("authoring.labelList.fields.guidelines")} value={label.guidelines} rows={4} onChange={(guidelines) => { onChange({ ...label, guidelines }); }} />
      <FieldProblems check={check} path={`labels.${index}.guidelines`} />
      <StringValues title={t("authoring.labelList.fields.examples")} values={label.examples} maximum={200} maximumLength={2000} onChange={(examples) => { onChange({ ...label, examples }); }} />
      <FieldProblems check={check} path={`labels.${index}.examples`} />
      <StringValues title={t("authoring.labelList.fields.ontology_terms")} values={label.ontology_terms} maximum={50} onChange={(ontology_terms) => { onChange({ ...label, ontology_terms }); }} />
      <FieldProblems check={check} path={`labels.${index}.ontology_terms`} />
    </FieldGroup>
  );
}

/** Separate values preserve multiline examples and every linked term on an edit round trip. */
function StringValues({ title, values, maximum, maximumLength, onChange }: { title: string; values: readonly string[]; maximum: number; maximumLength?: number; onChange: (values: string[]) => void }) {
  const { t } = useTranslation();
  return (
    <FieldGroup legend={title} className="space-y-1">
      {values.map((value, index) => (
        <div key={index} className="flex items-start gap-2">
          <textarea className={FORM_COMPACT_CONTROL_CLASS} aria-label={`${title} ${index + 1}`} rows={2} maxLength={maximumLength} value={value} onChange={(event) => { onChange(values.map((entry, at) => at === index ? event.target.value : entry)); }} />
          <AppButton variant="ghost" size="xs" aria-label={t("authoring.labelList.removeValue", { name: title, position: index + 1 })} onClick={() => { onChange(values.filter((_, at) => at !== index)); }}>{t("authoring.labelList.remove")}</AppButton>
        </div>
      ))}
      <AppButton variant="secondary" size="xs" disabled={values.length >= maximum} onClick={() => { onChange([...values, ""]); }}>{t("authoring.labelList.addValue", { name: title })}</AppButton>
    </FieldGroup>
  );
}
